import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useUIStore } from '@/store/uiStore';
import { Plus, Zap, Heart, Receipt, Repeat, ChevronRight, ChevronLeft, CheckSquare, Layers, Download, Rocket, Camera } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PickInventoryItemSheet } from '@/components/inventory/PickInventoryItemSheet';
import { MarkAsSoldDialog } from '@/components/item/MarkAsSoldDialog';
import { QuickPriceEditDialog } from '@/components/inventory/QuickPriceEditDialog';
import type { UserItem } from '@/types/userItem';
import { MVP_FEATURES } from '@/lib/mvpFeatures';

interface QuickCreateSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Action = {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
};

export function QuickCreateSheet({ open, onOpenChange }: QuickCreateSheetProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { openAddItemModal, openAddLotModal, requestInventorySelection, requestCollectrImport, requestRapidList, requestBulkPhotoUpload, requestRapidPhotograph } = useUIStore();

  // Picker + downstream dialog state — mounted as siblings so they survive sheet close.
  const [pickerMode, setPickerMode] = useState<null | 'sale' | 'trade'>(null);
  const [saleItem, setSaleItem] = useState<UserItem | null>(null);
  const [tradeItem, setTradeItem] = useState<UserItem | null>(null);
  const [extraTradeItems, setExtraTradeItems] = useState<UserItem[]>([]);

  const handlePickerSelect = (item: UserItem) => {
    if (pickerMode === 'sale') setSaleItem(item);
    setPickerMode(null);
  };

  const handleTradePickerConfirm = (items: UserItem[]) => {
    if (items.length === 0) return;
    const [first, ...rest] = items;
    setTradeItem(first);
    setExtraTradeItems(rest);
    setPickerMode(null);
  };

  const path = location.pathname;

  const close = () => onOpenChange(false);

  // Phase 8 convergence: the Radix Sheet close animation used to hold
  // pointer-events for ~150ms, forcing an artificial 180ms delay before
  // opening the target surface. Modern Radix releases the lock synchronously
  // once `open=false` propagates, so hand off on the next microtask instead
  // of the full close animation — `+` → target is now one animation.
  const afterClose = (fn: () => void) => {
    close();
    queueMicrotask(fn);
  };


  const addItem = (): Action => ({
    key: 'add-item',
    label: 'Add to Inventory',
    icon: Plus,
    onClick: () => {
      afterClose(() => {
        if (path !== '/inventory') navigate('/inventory');
        openAddItemModal();
      });
    },
  });

  // Quick Add removed — unified into a single openAddItemModal() entry point.
  // Keeping a placeholder so external action arrays don't blow up if referenced.


  const addBulkLot = (): Action => ({
    key: 'add-bulk-lot',
    label: 'Add Lot',
    icon: Layers,
    onClick: () => {
      afterClose(() => {
        if (path !== '/inventory') navigate('/inventory');
        openAddLotModal();
      });
    },
  });





  const importCollectr = (): Action => ({
    key: 'import-collectr',
    label: 'Import from Collectr',
    icon: Download,
    onClick: () => {
      afterClose(() => {
        if (path !== '/inventory') navigate('/inventory');
        requestCollectrImport();
      });
    },
  });

  const recordSale = (): Action => ({
    key: 'record-sale',
    label: 'Record a Sale',
    icon: Receipt,
    onClick: () => {
      afterClose(() => setPickerMode('sale'));
    },
  });

  const recordTrade = (): Action => ({
    key: 'record-trade',
    label: 'Record a Trade',
    icon: Repeat,
    onClick: () => {
      afterClose(() => setPickerMode('trade'));
    },
  });

  const selectItems = (): Action => ({
    key: 'select-items',
    label: 'Select Items',
    icon: CheckSquare,
    onClick: () => {
      afterClose(() => {
        if (path !== '/inventory') navigate('/inventory');
        requestInventorySelection();
      });
    },
  });

  const rapidList = (): Action => ({
    key: 'rapid-list',
    label: 'Rapid List',
    icon: Rocket,
    onClick: () => {
      afterClose(() => {
        if (path !== '/inventory') navigate('/inventory');
        requestRapidList();
      });
    },
  });

  const bulkPhotos = (): Action => ({
    key: 'bulk-photos',
    label: 'Bulk photograph',
    icon: Camera,
    onClick: () => {
      afterClose(() => {
        if (path !== '/inventory') navigate('/inventory');
        requestRapidPhotograph();
      });
    },
  });


  const [showRecordChoice, setShowRecordChoice] = useState(false);
  const [showAddChoice, setShowAddChoice] = useState(false);
  // 'forward' = drilling into a sub-menu, 'backward' = returning to root.
  // Drives the slide direction so users feel navigation, not a silent swap.
  const [navDir, setNavDir] = useState<'forward' | 'backward'>('forward');

  const goForward = (fn: () => void) => {
    setNavDir('forward');
    fn();
  };
  const goBack = (fn: () => void) => {
    setNavDir('backward');
    fn();
  };

  const recordSaleOrTrade = (): Action => ({
    key: 'record',
    label: 'Record a sale or trade',
    icon: Receipt,
    onClick: () => goForward(() => setShowRecordChoice(true)),
  });

  const addToInventory = (): Action => ({
    key: 'add-to-inventory',
    label: 'Add to Inventory',
    icon: Plus,
    primary: true,
    onClick: () => {
      afterClose(() => {
        if (path !== '/inventory') navigate('/inventory');
        openAddItemModal();
      });
    },
  });


  const actions: Action[] = [
    addToInventory(),
    rapidList(),
    bulkPhotos(),
    importCollectr(),
    recordSaleOrTrade(),
    selectItems(),
  ];



  const handleOpenChange = (o: boolean) => {
    // Always reset drill-down state on both open and close so reopening the
    // sheet always lands on the root menu, never a stale sub-choice.
    setShowRecordChoice(false);
    setShowAddChoice(false);
    setNavDir('forward');
    onOpenChange(o);
  };

  // Belt-and-suspenders: if the sheet is opened externally (parent flips
  // `open` to true) without a fresh handleOpenChange call, still snap back
  // to the root menu.
  useEffect(() => {
    if (open) {
      setShowRecordChoice(false);
      setShowAddChoice(false);
      setNavDir('forward');
    }
  }, [open]);

  const subChoice = showRecordChoice
    ? {
        title: 'Record a sale or trade',
        back: () => goBack(() => setShowRecordChoice(false)),
        rows: [recordSale(), recordTrade()],
      }
    : showAddChoice
      ? { title: 'Add to Inventory', back: () => goBack(() => setShowAddChoice(false)), rows: [addItem(), addBulkLot(), importCollectr()] }

      : null;



  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          className="bg-chrome border-white/[0.06] rounded-t-2xl p-0 pb-[env(safe-area-inset-bottom)]"
        >
          <SheetHeader className={subChoice ? "px-5 pt-4 pb-3 text-left" : "sr-only"}>
            <SheetTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
              {subChoice && (
                <button
                  type="button"
                  onClick={subChoice.back}
                  className="-ml-1 mr-1 inline-flex items-center justify-center w-7 h-7 rounded-full hover:bg-white/[0.06] text-foreground/70"
                  aria-label="Back"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}
              <span className={subChoice ? "sr-only" : ""}>
                {subChoice ? subChoice.title : 'Quick Create'}
              </span>
            </SheetTitle>
          </SheetHeader>

          <div
            key={subChoice ? subChoice.title : 'root'}
            className={cn(
              'px-3 pb-4 flex flex-col gap-1 max-h-[70dvh] overflow-y-auto',
              subChoice ? '' : 'pt-4',
              'animate-in fade-in-0 duration-200 ease-out',
              navDir === 'forward' ? 'slide-in-from-right-4' : 'slide-in-from-left-4',
            )}
          >
            {subChoice
              ? subChoice.rows.map((a) => <ActionRow key={a.key} action={a} primary={a.primary} />)
              : actions.map((a) => (
                  <ActionRow key={a.key} action={a} primary={a.primary} />
                ))}
          </div>
        </SheetContent>
      </Sheet>





      {/* Inventory picker (sale or trade) */}
      <PickInventoryItemSheet
        open={pickerMode !== null}
        onOpenChange={(o) => !o && setPickerMode(null)}
        multi={pickerMode === 'trade'}
        onSelect={handlePickerSelect}
        onConfirm={handleTradePickerConfirm}
        title={pickerMode === 'trade' ? 'Pick items to trade' : 'Pick item to mark as sold'}
      />

      {/* Mark as Sold dialog — fed by sale picker */}
      {saleItem && (
        <MarkAsSoldDialog
          open={!!saleItem}
          onOpenChange={(o) => !o && setSaleItem(null)}
          itemId={saleItem.id}
          itemTitle={saleItem.title}
          askingPriceCents={saleItem.asking_price_cents ?? null}
          costBasisCents={saleItem.cost_basis_cents ?? null}
          quantity={saleItem.quantity ?? 1}
        />
      )}

      {/* Trade flow — opens the unified item drawer where "Mark as Sold or Traded" lives */}
      {tradeItem && (
        <QuickPriceEditDialog
          item={tradeItem}
          open={!!tradeItem}
          onClose={() => {
            setTradeItem(null);
            setExtraTradeItems([]);
          }}
          defaultMode="traded"
          additionalGaveItems={extraTradeItems}
        />
      )}
    </>
  );
}

function ActionRow({ action, primary }: { action: Action; primary?: boolean }) {
  const Icon = action.icon;
  const disabled = !!action.disabled;
  return (
    <button
      type="button"
      onClick={action.onClick}
      disabled={disabled}
      aria-disabled={disabled}
      className={cn(
        'w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left',
        'transition-colors duration-150',
        disabled
          ? 'bg-white/[0.02] text-foreground/40 cursor-not-allowed'
          : primary
            ? 'bg-gradient-to-br from-[hsl(50,100%,72%)] to-[hsl(45,95%,60%)] text-black active:opacity-90'
            : 'bg-white/[0.04] hover:bg-white/[0.07] active:bg-white/[0.09] text-foreground',
      )}
    >
      <div
        className={cn(
          'flex items-center justify-center w-9 h-9 rounded-lg shrink-0',
          disabled ? 'bg-white/[0.04]' : primary ? 'bg-black/15' : 'bg-white/[0.06]',
        )}
      >
        <Icon
          className={cn(
            'w-[18px] h-[18px]',
            disabled ? 'text-foreground/30' : primary ? 'text-black' : 'text-foreground/80',
          )}
        />
      </div>
      <div className="flex-1 min-w-0 self-center">
        <div
          className={cn(
            'text-sm font-semibold leading-tight',
            disabled ? 'text-foreground/40' : primary ? 'text-black' : 'text-foreground',
          )}
        >
          {action.label}
        </div>
      </div>
      {!disabled && (
        <ChevronRight className={cn('w-4 h-4 shrink-0', primary ? 'text-black/60' : 'text-foreground/30')} />
      )}
    </button>
  );
}
